// A tableId must be unique among the table instances on one page. The check runs during render, so a
// duplicate instance renders its configuration error on its first render and never paints a table.
//
// Each tableId keeps the instances that committed it, in mount order, and the instances that rendered it
// but have not committed yet. The first of them owns the tableId; every other instance is a duplicate.
// A render that never commits leaves a pending claim behind, so every commit clears the pending claims and
// re-checks each instance holding that tableId, which corrects an instance that rendered against a stale one.

import { useId, useLayoutEffect, useReducer, useRef } from 'react'

const claims = new Map()

const entryFor = tableId => {
  let entry = claims.get(tableId)
  if (!entry) {
    entry = { committed: [], pending: new Set(), recheck: new Map() }
    claims.set(tableId, entry)
  }
  return entry
}

const ownerOf = entry =>
  entry.committed.length
    ? entry.committed[0]
    : entry.pending.values().next().value

const notify = (entry, except) => {
  entry.recheck.forEach((recheck, token) => {
    if (token !== except) recheck()
  })
}

/**
 * Whether another instance on the page already holds `tableId`.
 * @param {string} tableId
 * @returns {boolean}
 */
export const useDuplicateTableId = tableId => {
  const token = useId()
  const [, recheck] = useReducer(count => count + 1, 0)
  const rendered = useRef(false)

  let duplicate = false
  if (tableId) {
    const entry = entryFor(tableId)
    if (!entry.committed.includes(token)) entry.pending.add(token)
    duplicate = ownerOf(entry) !== token
  }
  rendered.current = duplicate

  useLayoutEffect(() => {
    if (!tableId) return undefined
    const entry = entryFor(tableId)
    entry.pending.clear()
    if (!entry.committed.includes(token)) entry.committed.push(token)
    entry.recheck.set(token, () => {
      if ((entry.committed[0] !== token) !== rendered.current) recheck()
    })
    entry.recheck.get(token)()
    notify(entry, token)
    return () => {
      entry.committed = entry.committed.filter(t => t !== token)
      entry.recheck.delete(token)
      if (!entry.committed.length && !entry.pending.size) claims.delete(tableId)
      else notify(entry, token)
    }
  }, [tableId, token])

  return duplicate
}
